import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MapPin, Navigation, Check } from "lucide-react";
import { toast } from "sonner";

// Fix default icons (Leaflet's default assets break with webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Center: Città di San Marino
const SM_CENTER = [43.9424, 12.4578];

function ClickPicker({ position, onPick }) {
  useMapEvents({
    click(e) {
      onPick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return position ? <Marker position={position} /> : null;
}

export default function LocationPicker({ open, onClose, onShare }) {
  const [pos, setPos] = useState(null);
  const [label, setLabel] = useState("");
  const [useCurrent, setUseCurrent] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPos(null); setLabel(""); setUseCurrent(false);
    }
  }, [open]);

  const getCurrent = () => {
    if (!navigator.geolocation) return toast.error("Geolocalizzazione non supportata");
    setUseCurrent(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const ll = [p.coords.latitude, p.coords.longitude];
        setPos(ll);
        if (mapRef.current) mapRef.current.setView(ll, 15);
        setUseCurrent(false);
      },
      () => { toast.error("Impossibile ottenere la posizione"); setUseCurrent(false); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const share = () => {
    if (!pos) return toast.error("Seleziona un punto sulla mappa");
    onShare({ lat: pos[0], lng: pos[1], label });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-[#e86a24]"/>Condividi posizione</DialogTitle>
          <DialogDescription>Per l'incontro puoi inviare solo una posizione dalla mappa. Tocca la mappa per scegliere un punto.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <Button onClick={getCurrent} disabled={useCurrent} variant="outline" className="flex-1 h-10">
              <Navigation className="w-4 h-4 mr-2"/> {useCurrent ? "Rilevamento..." : "La mia posizione"}
            </Button>
            <Input placeholder="Etichetta (es: Piazza della Libertà)" value={label} onChange={(e) => setLabel(e.target.value)} className="flex-1 h-10"/>
          </div>

          <div className="h-[360px] rounded-xl overflow-hidden border-2 border-slate-200">
            <MapContainer center={SM_CENTER} zoom={13} style={{ height: "100%", width: "100%" }} ref={mapRef}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ClickPicker position={pos} onPick={setPos} />
            </MapContainer>
          </div>

          {pos && (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              Punto selezionato: <span className="font-mono font-bold">{pos[0].toFixed(5)}, {pos[1].toFixed(5)}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Annulla</Button>
          <Button onClick={share} className="flex-1 bg-[#e86a24] hover:bg-[#c8561a]"><Check className="w-4 h-4 mr-2"/>Invia posizione</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
